import { useEffect, useRef, useState, type RefObject } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { Loader2, Sparkles } from 'lucide-react';
import { GbpPostInputSchema, scheduleProblem, type GbpPostType } from '@pageboss/shared';
import { Input } from '@/components/ui/input';
import { SegmentedControl } from '@/components/inspector/segmented-control';
import { buttonVariants } from '@/components/ui/button';
import { api, type ApiError } from '@/lib/api';
import type { AiContentResponse, GbpPostSendResponse } from '@/lib/types';
import { cn } from '@/lib/utils';
import { fieldClasses } from './post-field-styles';
import { PostScheduleRow } from './post-schedule-row';

// Compose → preview-in-place → Publish (S3 §4.1). The same Zod schema the
// server enforces validates here first, so a too-long summary or a missing
// event window never costs a round trip. Publish failures NEVER clear the
// form — the server saved the row (D5), and the message says so; the list
// below picks up the saved row on the next onChanged() refetch.
//
// Visuals per the S3 screen-design addendum §B: pill-toggle type tabs
// (billing.tsx idiom), AI-brief row above the textarea, visible-label
// text field with a helper+counter row, a two-tone (danger/warning) send
// banner, "editTarget" lets a permanently-rejected list row hand this panel
// its type+summary to fix and resend (the wire type carries no cta/event/
// offer payload, so only those two fields can be restored — an honest
// limit, not an oversight).

// Post type — one of three, so it is the shared SegmentedControl (design
// audit T14, T7's parked list). It used to be a hand-rolled `role="group"` +
// `aria-pressed` strip: honest markup, but the fourth spelling of a control
// this dashboard already has one of, and `aria-checked` in a radiogroup says
// "pick one of these" where `aria-pressed` only says "this is on".
//
// The three lucide glyphs went with it. They were `aria-hidden` decoration
// beside labels that already read Update / Offer / Event, and keeping them
// would have meant widening the primitive for one call site.

const TYPES: ReadonlyArray<{ value: GbpPostType; label: string }> = [
  { value: 'update', label: 'Update' },
  { value: 'offer', label: 'Offer' },
  { value: 'event', label: 'Event' },
];

const CTA_OPTIONS = [
  { value: '', label: 'No button' },
  { value: 'LEARN_MORE', label: 'Learn more' },
  { value: 'BOOK', label: 'Book' },
  { value: 'ORDER', label: 'Order' },
  { value: 'SHOP', label: 'Shop' },
  { value: 'SIGN_UP', label: 'Sign up' },
  { value: 'CALL', label: 'Call' },
] as const;

const MAX_SUMMARY = 1500;

// Server error code → plain sentence + which banner tone to use. Never
// render a raw code (CLAUDE.md security rule extends to UX honesty here).
// Tone follows part-status.tsx's durable(danger)/self-heals(warning) split:
// a rejected/refused post and a stale Google connection both need the
// owner to DO something; a network hiccup or a spent quota do not need
// edits to this post's text.
const POST_ERRORS: Record<string, { message: string; tone: 'danger' | 'warning' }> = {
  post_rejected: {
    message: "Google didn't accept this post. Edit the text and try again — your draft is saved.",
    tone: 'danger',
  },
  compliance_refused: {
    message:
      "We couldn't draft this one — the wording came out against Google's post rules. Try writing it yourself.",
    tone: 'danger',
  },
  reauth_needed: {
    message: 'Your Google connection needs to be renewed. Reconnect from the top of this page.',
    tone: 'danger',
  },
  gbp_unreachable: {
    message: "Couldn't reach Google just now. Your draft is saved — try again in a moment.",
    tone: 'warning',
  },
  quota_exceeded: {
    message: "You've used all your AI helps for this month. You can still write the post yourself.",
    tone: 'warning',
  },
  locked: {
    message: "Your current plan doesn't include this anymore. Check your plan in Billing.",
    tone: 'warning',
  },
  not_verified: {
    message: "Google hasn't finished verifying this listing yet, so changes can't be published.",
    tone: 'warning',
  },
  schedule_in_past: {
    message: 'That time has already passed — pick a moment in the future.',
    tone: 'danger',
  },
  schedule_too_far: {
    message: 'That date is more than a year out — pick something closer.',
    tone: 'danger',
  },
  not_connected: {
    message: "This site's Google connection was removed. Reconnect from the top of this page.",
    tone: 'danger',
  },
  gbp_unavailable: {
    message: "Google features aren't available right now. Try again later.",
    tone: 'warning',
  },
};
const FALLBACK_ERROR = { message: "That didn't go through. Your text is still here — try again.", tone: 'warning' as const };

function postErrorInfo(code: string | undefined): { message: string; tone: 'danger' | 'warning' } {
  return (code ? POST_ERRORS[code] : undefined) ?? FALLBACK_ERROR;
}

// Server answers `{ error: '<code>' }` (or `{ error, post }`) on failure —
// ApiError's `.body` is the parsed JSON, so read the code off it rather
// than the derived message (same mechanism as review-reply-box.tsx).
function apiErrorCode(err: unknown): string | undefined {
  const body = (err as ApiError | undefined)?.body;
  return body && typeof body === 'object' && 'error' in body
    ? String((body as { error: unknown }).error)
    : undefined;
}

const textareaClasses = cn(fieldClasses, 'h-auto py-2 leading-relaxed resize-y min-h-[6rem]');

export interface GbpPostEditTarget {
  type: GbpPostType;
  summary: string;
  // The row's stored cta/event/offer, when it had any (fix round 1: without
  // this, "Edit & resend" silently dropped an offer's coupon/CTA/terms on
  // republish — GbpPostRowWire.payload now carries them through).
  payload?: {
    cta?: { actionType: string; url?: string };
    event?: { title: string; start: { date: string; time?: string }; end: { date: string; time?: string } };
    offer?: { couponCode?: string; redeemUrl?: string; terms?: string };
  };
  // FIX 5 (S4 review): a permanently-rejected recurring row's cadence
  // preferences, when it had any — restored into the repeat/repeatUntil
  // fields on "Edit & resend" (the schedule itself stays off/now by
  // default; the row's original time already passed, so restoring the
  // cadence prefs alone is enough for the owner to re-pick a new time).
  recurrence?: { cadence: 'weekly' | 'monthly'; until?: string };
}

export function GbpPostCompose({
  siteId,
  canWrite,
  editTarget,
  onEditConsumed,
  onChanged,
}: {
  siteId: string;
  canWrite: boolean;
  /** Set by a "Edit & resend" click on a permanently-rejected list row. */
  editTarget: GbpPostEditTarget | null;
  onEditConsumed: () => void;
  onChanged: (announcement?: string) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const businessId = useBusinessId() ?? '';
  const [type, setType] = useState<GbpPostType>('update');
  const [summary, setSummary] = useState('');
  const [ctaType, setCtaType] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [terms, setTerms] = useState('');
  const [brief, setBrief] = useState('');
  const [when, setWhen] = useState<'now' | 'schedule'>('now');
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('');
  const [repeat, setRepeat] = useState<'' | 'weekly' | 'monthly'>('');
  const [repeatUntil, setRepeatUntil] = useState('');
  const [busy, setBusy] = useState<'draft' | 'publish' | null>(null);
  const [error, setError] = useState<{ message: string; tone: 'danger' | 'warning' } | null>(null);
  const summaryRef = useRef<HTMLTextAreaElement | null>(null);
  // Mirrors review-reply-box.tsx's focusPending mechanism: a landed AI draft
  // is otherwise a silent DOM update — nothing tells a keyboard/screen-reader
  // user the text arrived. Set right before the draft lands; consumed once
  // the DOM reflects it. Never true on mount or on the editTarget restore
  // below (that already has its own scroll+focus).
  const focusPending = useRef(false);

  useEffect(() => {
    if (!focusPending.current) return;
    focusPending.current = false;
    const el = summaryRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [summary]);

  useEffect(() => {
    if (!editTarget) return;
    setType(editTarget.type);
    setSummary(editTarget.summary);
    // Restore the FULL post, not just its text — a rejected offer's coupon
    // code or CTA button must come back too, or a "fixed" republish quietly
    // ships without them. Fields the payload genuinely lacks reset to empty
    // (this form has no time-of-day inputs, so payload.event's optional
    // `time` — never captured or sent by this form either — is not
    // restored; that's an existing gap, not a new one).
    setCtaType(editTarget.payload?.cta?.actionType ?? '');
    setCtaUrl(editTarget.payload?.cta?.url ?? '');
    setTitle(editTarget.payload?.event?.title ?? '');
    setStartDate(editTarget.payload?.event?.start.date ?? '');
    setEndDate(editTarget.payload?.event?.end.date ?? '');
    setCouponCode(editTarget.payload?.offer?.couponCode ?? '');
    setTerms(editTarget.payload?.offer?.terms ?? '');
    // FIX 5: restore the cadence prefs only — `when` stays 'now' (its
    // default), so the owner picks a fresh date/time rather than resending
    // at the row's original (already-past) scheduledAt.
    setRepeat(editTarget.recurrence?.cadence ?? '');
    setRepeatUntil(editTarget.recurrence?.until ?? '');
    setError(null);
    onEditConsumed();
    summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    summaryRef.current?.focus();
    // onEditConsumed is stable from the parent's useState setter; only react
    // to a genuinely new edit target landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTarget]);

  if (!canWrite) {
    return (
      <p className="text-sm text-text-tertiary">
        Posting unlocks once Google finishes verifying this listing.
      </p>
    );
  }

  const needsWindow = type !== 'update';

  function buildInput(): unknown {
    return {
      type,
      summary: summary.trim(),
      ...(ctaType
        ? { cta: { actionType: ctaType, ...(ctaType !== 'CALL' && ctaUrl ? { url: ctaUrl } : {}) } }
        : {}),
      ...(needsWindow
        ? { event: { title: title.trim(), start: { date: startDate }, end: { date: endDate } } }
        : {}),
      ...(type === 'offer' && (couponCode.trim() || terms.trim())
        ? {
            offer: {
              ...(couponCode.trim() ? { couponCode: couponCode.trim() } : {}),
              ...(terms.trim() ? { terms: terms.trim() } : {}),
            },
          }
        : {}),
      ...(when === 'schedule'
        ? {
            schedule: {
              // Local wall-clock → epoch: the owner means THEIR 9am.
              publishAtMs: new Date(`${schedDate}T${schedTime || '00:00'}`).getTime(),
              // FIX 4 (S4 review): the owner's fixed UTC-minute offset AT
              // SCHEDULE TIME (getTimezoneOffset() convention) — the server
              // uses this to derive the recurrence rule's startedOn/
              // dayOfMonth from the OWNER'S local calendar day, not the
              // server's UTC one (a US evening pick can already be
              // tomorrow in UTC).
              tzOffsetMinutes: new Date().getTimezoneOffset(),
              ...(type === 'update' && repeat
                ? {
                    recurrence: {
                      cadence: repeat,
                      ...(repeatUntil ? { until: repeatUntil } : {}),
                    },
                  }
                : {}),
            },
          }
        : {}),
    };
  }

  async function draftWithAi() {
    if (brief.trim().length === 0) {
      setError({ message: 'Tell the assistant what the post is about first.', tone: 'warning' });
      return;
    }
    setBusy('draft');
    setError(null);
    try {
      const res = await api.post<AiContentResponse<{ summary: string; eventTitle?: string }>>(
        locationApi(businessId, siteId, 'gbp/posts/draft'),
        { type, brief: brief.trim() },
      );
      setSummary(res.result.summary);
      if (res.result.eventTitle && !title) setTitle(res.result.eventTitle);
      focusPending.current = true;
    } catch (err) {
      setError(postErrorInfo(apiErrorCode(err)));
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    const parsed = GbpPostInputSchema.safeParse(buildInput());
    if (!parsed.success) {
      setError({ message: parsed.error.issues[0]?.message ?? 'Check the highlighted fields.', tone: 'danger' });
      return;
    }
    // Past/too-far is a NOW-relative judgment that can only be made at
    // click time (Date.now() at render would go stale) — so it runs here,
    // not as a field-level static check, and lands in the same general
    // banner as every other publish failure (screen-design addendum §B).
    if (parsed.data.schedule) {
      const problem = scheduleProblem(parsed.data.schedule.publishAtMs, Date.now());
      if (problem) {
        setError(postErrorInfo(problem === 'past' ? 'schedule_in_past' : 'schedule_too_far'));
        return;
      }
    }
    setBusy('publish');
    setError(null);
    try {
      await api.post<GbpPostSendResponse>(locationApi(businessId, siteId, 'gbp/posts'), parsed.data);
      setSummary('');
      setBrief('');
      setTitle('');
      setStartDate('');
      setEndDate('');
      setCouponCode('');
      setTerms('');
      setCtaType('');
      setCtaUrl('');
      onChanged(when === 'schedule' ? 'Post scheduled.' : 'Posted to Google.');
      setWhen('now');
      setSchedDate('');
      setSchedTime('');
      setRepeat('');
      setRepeatUntil('');
    } catch (err) {
      // The row IS saved server-side on every failure — the list below will
      // show it (with a retry or an edit-and-resend), and the form here
      // keeps the owner's text too.
      setError(postErrorInfo(apiErrorCode(err)));
      onChanged();
    } finally {
      setBusy(null);
    }
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void publish();
      }}
    >
      <SegmentedControl<GbpPostType>
        ariaLabel="Post type"
        value={type}
        options={TYPES}
        onChange={setType}
      />
      <PostAiBriefRow brief={brief} setBrief={setBrief} busy={busy} onDraft={() => void draftWithAi()} />
      <PostSummaryField summaryRef={summaryRef} summary={summary} setSummary={setSummary} />

      <PostScheduleRow
        type={type}
        when={when}
        setWhen={setWhen}
        schedDate={schedDate}
        setSchedDate={setSchedDate}
        schedTime={schedTime}
        setSchedTime={setSchedTime}
        repeat={repeat}
        setRepeat={setRepeat}
        repeatUntil={repeatUntil}
        setRepeatUntil={setRepeatUntil}
      />

      <PostSecondaryFields
        type={type}
        needsWindow={needsWindow}
        title={title}
        setTitle={setTitle}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        terms={terms}
        setTerms={setTerms}
        ctaType={ctaType}
        setCtaType={setCtaType}
        ctaUrl={ctaUrl}
        setCtaUrl={setCtaUrl}
      />

      {error ? (
        <p
          role="alert"
          className={cn(
            'rounded-md px-3 py-2 text-sm',
            error.tone === 'danger' ? 'bg-danger-bg text-danger-fg' : 'bg-warning-bg text-warning-fg',
          )}
        >
          {error.message}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={busy !== null || summary.trim().length === 0}
          className={buttonVariants({ size: 'sm' })}
        >
          {busy === 'publish'
            ? when === 'schedule'
              ? 'Scheduling…'
              : 'Publishing…'
            : when === 'schedule'
              ? 'Schedule post'
              : 'Publish to Google'}
        </button>
        <span className="text-xs text-text-tertiary">
          {when === 'schedule' ? 'Goes live at the time you pick.' : 'Goes live on your Google listing.'}
        </span>
      </div>
    </form>
  );
}

function PostAiBriefRow(props: {
  brief: string;
  setBrief: (v: string) => void;
  busy: 'draft' | 'publish' | null;
  onDraft: () => void;
}) {
  const { brief, setBrief, busy, onDraft } = props;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor="post-brief" className="text-xs font-medium text-text-secondary">
        Want the assistant to write it? Say what it's about (uses 1 AI help)
      </label>
      <div className="flex gap-2">
        <Input
          id="post-brief"
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          maxLength={2000}
          placeholder="e.g. new caramel flavor for August"
          className="h-10 flex-1"
        />
        <button
          type="button"
          disabled={busy !== null}
          onClick={onDraft}
          className={buttonVariants({ size: 'sm', variant: 'outline' })}
        >
          {busy === 'draft' ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
          )}
          {busy === 'draft' ? 'Writing…' : 'Write with AI'}
        </button>
      </div>
    </div>
  );
}

function PostSummaryField(props: {
  summaryRef: RefObject<HTMLTextAreaElement | null>;
  summary: string;
  setSummary: (v: string) => void;
}) {
  const { summaryRef, summary, setSummary } = props;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor="post-summary" className="text-sm font-medium text-foreground">
        Post text
      </label>
      <textarea
        ref={summaryRef}
        id="post-summary"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        maxLength={MAX_SUMMARY}
        rows={4}
        className={textareaClasses}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-text-tertiary">~1500 characters</span>
        <span
          className={cn(
            'text-xs tabular-nums',
            summary.length >= MAX_SUMMARY - 100 ? 'text-danger-fg' : 'text-text-tertiary',
          )}
        >
          {summary.length}/{MAX_SUMMARY}
        </span>
      </div>
    </div>
  );
}

// Event/offer window + coupon/terms + CTA button fields — split out to keep
// GbpPostCompose readable (CLAUDE.md: components under ~200 lines).
function PostSecondaryFields(props: {
  type: GbpPostType;
  needsWindow: boolean;
  title: string;
  setTitle: (v: string) => void;
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  couponCode: string;
  setCouponCode: (v: string) => void;
  terms: string;
  setTerms: (v: string) => void;
  ctaType: string;
  setCtaType: (v: string) => void;
  ctaUrl: string;
  setCtaUrl: (v: string) => void;
}) {
  const {
    type,
    needsWindow,
    title,
    setTitle,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    couponCode,
    setCouponCode,
    terms,
    setTerms,
    ctaType,
    setCtaType,
    ctaUrl,
    setCtaUrl,
  } = props;

  return (
    <>
      {needsWindow ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label htmlFor="post-title" className="text-sm font-medium text-foreground">
              {type === 'event' ? 'Event title' : 'Offer title'}
            </label>
            <input
              id="post-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={58}
              className={fieldClasses}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="post-start" className="text-sm font-medium text-foreground">
              {type === 'event' ? 'Starts' : 'Valid from'}
            </label>
            <input
              id="post-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={fieldClasses}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="post-end" className="text-sm font-medium text-foreground">
              {type === 'event' ? 'Ends' : 'Valid until'}
            </label>
            <input
              id="post-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={fieldClasses}
            />
          </div>
        </div>
      ) : null}

      {type === 'offer' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="post-coupon" className="text-sm font-medium text-foreground">
              Coupon code (optional)
            </label>
            <input
              id="post-coupon"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              maxLength={58}
              className={fieldClasses}
            />
          </div>
          <div className="flex flex-col gap-1 sm:col-span-2">
            <label htmlFor="post-terms" className="text-sm font-medium text-foreground">
              Terms (optional)
            </label>
            <textarea
              id="post-terms"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              maxLength={5000}
              rows={2}
              className={cn(fieldClasses, 'h-auto py-2 leading-relaxed resize-y min-h-[3.5rem]')}
            />
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="post-cta" className="text-sm font-medium text-foreground">
            Button (optional)
          </label>
          <select
            id="post-cta"
            value={ctaType}
            onChange={(e) => setCtaType(e.target.value)}
            className={fieldClasses}
          >
            {CTA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {ctaType && ctaType !== 'CALL' ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="post-cta-url" className="text-sm font-medium text-foreground">
              Button link
            </label>
            <input
              id="post-cta-url"
              type="url"
              value={ctaUrl}
              onChange={(e) => setCtaUrl(e.target.value)}
              placeholder="https://…"
              className={fieldClasses}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
